import {
  type GenericPolyElement,
  type PointBezierHandle,
  type PolyPlugin,
} from '..';
import { getElementBounds } from '../../../lib/bound';
import { reversionElement } from '../../../lib/element';
import { getPlugin } from '../../../lib/plugin';
import { generateTransformHandle } from '../../../lib/selection';
import { useEditorStore } from '../../../stores/useEditorStore';
import { type Bounds } from '../../../utils/editor/bounds';
import {
  distance,
  normalizeRadians,
  pointRotateRads,
  type Point,
} from '../../../utils/math';

import { offsetHandles } from './bezierHandles';
import { getBoundsWithBezier, getRelativePolyBounds } from './bounds';
import { isPointRounded } from './point';

export const getPolyTransformHandles: NonNullable<
  NonNullable<PolyPlugin['selection']>['getTransformHandles']
> = (element, { state }) => {
  if (!element.isVisible) return {};

  const { polygonPointsVisible } = useEditorStore.getState();

  const isNewElement = element.id === state.newElement?.id;
  if (
    !polygonPointsVisible &&
    getPlugin(element.type).selection?.hasVisiblePointHandles &&
    !isNewElement
  ) {
    return {};
  }

  const size = 10;
  const handleWidth = size / state.zoom.value;
  const handleHeight = size / state.zoom.value;

  const [offsetX, offsetY] = [-handleWidth / 2, -handleHeight / 2];

  const [, , , , cx, cy] = getElementBounds(element);

  return element.points.reduce<Record<string, Bounds>>((acc, point, index) => {
    const x = element.x + point[0] + offsetX;
    const y = element.y + point[1] + offsetY;

    const bounds = generateTransformHandle(
      x,
      y,
      handleWidth,
      handleHeight,
      cx,
      cy,
      element.angle,
    );

    acc[index] = bounds;

    if (element.bezierHandles?.length === element.points.length) {
      const handle = element.bezierHandles[index];

      const beforeX = element.x + handle.before[0] + offsetX;
      const beforeY = element.y + handle.before[1] + offsetY;

      const afterX = element.x + handle.after[0] + offsetX;
      const afterY = element.y + handle.after[1] + offsetY;

      if (!isPointRounded(point, handle)) return acc;

      const beforeBounds = generateTransformHandle(
        beforeX,
        beforeY,
        handleWidth,
        handleHeight,
        cx,
        cy,
        element.angle,
      );

      acc[`${index}-handleBefore`] = beforeBounds;

      const afterBounds = generateTransformHandle(
        afterX,
        afterY,
        handleWidth,
        handleHeight,
        cx,
        cy,
        element.angle,
      );

      acc[`${index}-handleAfter`] = afterBounds;
    }

    return acc;
  }, {});
};

const getRelativeSize = (
  oldDimension: number,
  distance: number,
  isGrowing: boolean,
  shouldResize: boolean,
) => {
  if (!shouldResize) return 1;

  return isGrowing
    ? (oldDimension + distance) / oldDimension
    : (oldDimension - distance) / oldDimension;
};

const getNewResizeAxis = (
  oldAxis: number,
  dimension: number,
  distance: number,
  isMainAxis: boolean,
  isOtherAxis: boolean,
) => {
  if (!isMainAxis && !isOtherAxis) return oldAxis;

  if (isMainAxis) {
    return Math.min(oldAxis + distance, oldAxis + dimension);
  }
  if (isOtherAxis && distance > -dimension) return oldAxis;

  return oldAxis + distance + dimension;
};

export const resizePoly = (
  transform: string,
  originalElement: GenericPolyElement,
  pointer: Point,
  origin: Point,
) => {
  const directions = transform.split('');

  const [rotatedPointerX, rotatedPointerY] = pointRotateRads(
    pointer,
    origin,
    normalizeRadians(-originalElement.angle),
  );

  const dx = rotatedPointerX - origin[0];
  const dy = rotatedPointerY - origin[1];

  const isGrowingX =
    (directions.includes('e') && dx > 0) ||
    (directions.includes('w') && dx < 0);

  const isGrowingY =
    (directions.includes('s') && dy > 0) ||
    (directions.includes('n') && dy < 0);

  const relativeSizeX = getRelativeSize(
    originalElement.width,
    Math.abs(dx),
    isGrowingX,
    directions.includes('e') || directions.includes('w'),
  );

  const relativeSizeY = getRelativeSize(
    originalElement.height,
    Math.abs(dy),
    isGrowingY,
    directions.includes('s') || directions.includes('n'),
  );

  let updatedPoints = originalElement.points.map<Point>((point) => [
    point[0] * relativeSizeX,
    point[1] * relativeSizeY,
  ]);

  let updatedHandles = originalElement.bezierHandles?.map<PointBezierHandle>(
    (handle) => ({
      before: [
        handle.before[0] * relativeSizeX,
        handle.before[1] * relativeSizeY,
      ],
      after: [handle.after[0] * relativeSizeX, handle.after[1] * relativeSizeY],
    }),
  );

  const [minX, minY] = getBoundsWithBezier(updatedPoints, updatedHandles);

  updatedPoints = offsetPoints(updatedPoints, -minX, -minY);

  updatedHandles = offsetHandles(updatedHandles, -minX, -minY);

  const newWidth = Math.abs(originalElement.width * relativeSizeX);
  const newHeight = Math.abs(originalElement.height * relativeSizeY);

  const newX = getNewResizeAxis(
    originalElement.x,
    originalElement.width,
    dx,
    directions.includes('w'),
    directions.includes('e'),
  );

  const newY = getNewResizeAxis(
    originalElement.y,
    originalElement.height,
    dy,
    directions.includes('n'),
    directions.includes('s'),
  );

  const updated: GenericPolyElement = {
    ...originalElement,
    height: newHeight,
    points: updatedPoints,
    bezierHandles: updatedHandles,
    width: newWidth,
    x: newX,
    y: newY,
    ...reversionElement(originalElement, ['bounds', 'canvas', 'path']),
  };

  updated.shape = getPlugin(updated.type).collision.calculateShape(updated);

  return updated;
};

export const offsetPoints = (
  points: Point[],
  dx: number,
  dy: number,
): Point[] => {
  return points.map((point) => [point[0] + dx, point[1] + dy]);
};

export const normalizePolyToBounds = (
  points: Point[],
  bezierHandles: PointBezierHandle[] | undefined,
  originX: number,
  originY: number,
): {
  points: Point[];
  bezierHandles: PointBezierHandle[] | undefined;
  x: number;
  y: number;
  width: number;
  height: number;
} => {
  const [xMin, yMin, xMax, yMax] = bezierHandles
    ? getBoundsWithBezier(points, bezierHandles)
    : getRelativePolyBounds(points);

  const normalizedPoints = offsetPoints(points, -xMin, -yMin);
  const normalizedHandles = offsetHandles(bezierHandles, -xMin, -yMin);

  return {
    points: normalizedPoints,
    bezierHandles: normalizedHandles,
    x: originX + xMin,
    y: originY + yMin,
    width: distance(xMin, xMax),
    height: distance(yMin, yMax),
  };
};
